import { useState } from 'react'

import { money } from './estimate.js'

// One card on the board.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE DEGRADED STATES ARE THE POINT, NOT THE HAPPY PATH
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF names four a card must survive, and all four are reachable today:
// no photo, no cost at all, labeled-absent enrichment, and `removed`. They are
// not edge cases — the mock's own sample data has an entry with no photo and no
// amount whose only cost information is somebody repeating what a host said on
// the phone. A board that renders that badly is a board people stop pasting
// into at 1am, which is the moment it exists for.
//
// ⚠️ THE STRIPE IS STILL NOT A TODO — IT IS NOW THE **THIRD** STATE, NOT THE
// ONLY ONE. It read "unfurling is T8, so the stripe IS the correct render
// here" until T8 landed. There are three states now and each is a different
// fact about one card:
//
//   * a photo         — the link was unfurled and the page published one;
//   * the stripe      — a link, and no photo: not fetched yet, no OpenGraph
//                       tags, or a refusal. All three look the same on purpose
//                       (§8: refusals reveal nothing about what is behind the
//                       address), and a photo may still arrive on the feed.
//                       P6-82 added the way to ask again — see `onPreview`
//                       below, which is offered IDENTICALLY for all three, so
//                       the state is still one state and not a diagnosis;
//   * "no photo · added by hand" — no link at all, so none is ever coming.
//
// The stripe's promise is honest in a way the labeled state's would not be, and
// that is the whole reason they are two states rather than one.
//
// ⚠️ ENRICHMENT IS ABSENT, NOT LABELED-ABSENT. Spec T10 is explicit: "before
// this slice exists the grid OMITS enrichment fields entirely; labeled-absence
// is the designed state for provider failure once the pipeline exists, not for
// the pipeline's non-existence." So `enrichment` below defaults to absent and
// the board never passes it — no "distance unavailable" chip appears on a real
// card today, because there is no distance pipeline for it to be a report
// about. The prop and its amber render exist so the state is BUILT and
// demonstrably survivable (src/test/trips/card-states.test.jsx drives it), and
// so T10 has a seam rather than a rewrite.

/** Stable per-person avatar colour.
 *
 * 🔴 A COLOUR, AND DELIBERATELY NOT A NAME. ADR-0003 D2: "NO USER TABLE, and
 * none is implied here... the bot already keys every store by that id, so a
 * user record would be a second copy of something we already have." The board
 * therefore knows WHO added an entry — a Discord id — and has no way at all to
 * turn that into "Wy". The mock's named avatars ("Wy added it") need a name
 * source this slice does not have and must not invent one for; see the PR body,
 * where this is recorded as found-and-not-fixed rather than quietly dropped.
 *
 * What survives is the part that still works: the same person is the same
 * colour on every card, so "these three were all added by one person" reads at
 * a glance even when that person cannot be named.
 */
const AVATAR_TOKENS = [
  '--d2d-accent',
  '--d2d-accent-alt',
  '--d2d-positive',
  '--d2d-ember-300',
  '--d2d-dusk-300',
  '--d2d-mist-300',
]

export function avatarToken(userId) {
  const id = String(userId ?? '')
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_TOKENS[hash % AVATAR_TOKENS.length]
}

/** The host of a stored link, for the source chip ("AIRBNB.CO.UK").
 *
 * Parsed rather than sliced: a hand-rolled split on '/' would render whatever
 * is between the second and third slash, and on a malformed value that is not a
 * host. Anything unparseable gets no chip at all.
 */
export function sourceLabel(url) {
  if (typeof url !== 'string' || url === '') return null
  try {
    const host = new URL(url).hostname.replace(/^www\./, '')
    return host === '' ? null : host.toUpperCase()
  } catch {
    return null
  }
}

/** The unfurled photo for an entry, or `null`. **P5-3 T8.**
 *
 * 🔴 THE BROWSER IS THE THING THAT FETCHES THIS, WHICH IS WHY THERE IS A CHECK
 * HERE AS WELL AS IN THE SERVICE. `trips/utils/unfurl.py::_image` refuses a
 * `javascript:` or private-literal `og:image` before storing it; this refuses
 * the same shapes before *rendering* one. They are not a duplicated guard —
 * they protect two different parties. The service's stops our database
 * carrying a value that would make the site issue a request on somebody's
 * machine; this one is the last thing between a stored value and that request
 * actually happening, and it is the only one that still applies to a row
 * written before the service check existed.
 *
 * ⚠️ Deliberately narrower than the service's: no port rule, no host rules,
 * because the browser's own protections cover what a plain `<img>` can do and
 * a stricter rule here would silently drop photos that are fine. The one thing
 * it must refuse is a scheme that executes.
 */
export function photoUrl(entry) {
  const raw = entry?.unfurled?.image_url
  if (typeof raw !== 'string' || raw === '') return null
  try {
    const parsed = new URL(raw)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : null
  } catch {
    return null
  }
}

function countAt(entry, level) {
  const at = entry?.preferences?.[level]
  return Array.isArray(at) ? at.length : 0
}

export default function EntryCard({
  entry,
  you,
  onVote,
  onRemove,
  busy = false,
  enrichment,
  // ⚠️ P5-3 T9, and it defaults to FALSE so the board is byte-identical to what
  // T4 shipped. An archived place's board is a record of what people decided;
  // the counts are the whole point of reading it, so they stay — as text. What
  // goes is the ability to change them, and the way that is expressed is that
  // there is no button to press rather than a button that refuses.
  readOnly = false,
  // P6-82. "Try for a preview", offered only when a caller supplies a handler.
  //
  // 🔴 DEFAULT NULL, SO EVERY EXISTING SURFACE IS BYTE-IDENTICAL. The compare
  // screen renders these same cards and passes nothing, so nothing appears
  // there; T4's and T9's tests are untouched for the right reason rather than
  // by luck.
  //
  // ⚠️ IT SAYS NOTHING ABOUT WHY THERE IS NO PHOTO, and must not learn to. The
  // header above names three causes for the stripe — not fetched yet, no
  // OpenGraph tags, refused — and they look the same on purpose (§8). This
  // button is offered identically for all three, so pressing it is a request
  // rather than a diagnosis, and a card still reveals nothing about what is
  // behind the address.
  onPreview = null,
  // True while THIS card's preview is in flight. Board-wide `busy` would be
  // wrong: six seconds on somebody else's web server is not a reason nobody
  // else can vote.
  previewing = false,
}) {
  const mine = entry.your_preference ?? null
  const mustCount = countAt(entry, 'must')
  const keenCount = countAt(entry, 'interested')

  const amount = entry.cost ? money(entry.cost.amount, entry.cost.currency) : null
  const basisLabel = entry.cost?.basis === 'flat' ? 'flat' : 'per person'
  const source = sourceLabel(entry.url)
  const hasPhotoSlot = Boolean(entry.url)
  const addedByYou = you != null && entry.added_by === you

  // ⚠️ A photo URL that 404s falls back to the stripe rather than to a broken
  // image icon. The superseded spec called this out and it is still true:
  // "those URLs rot, exactly like Discord's own attachment URLs … a broken
  // thumbnail is cosmetic and acceptable." Cosmetic is not the same as fine to
  // render, and the stripe is already the correct state for "a link with no
  // photo".
  const [photoBroken, setPhotoBroken] = useState(false)
  const photo = photoBroken ? null : photoUrl(entry)

  // "clicking the active level clears it" — HANDOFF, and the service models it
  // as deleting the row because there is no `level: 'none'` to set.
  const vote = (level) => onVote?.(entry, mine === level ? null : level)

  return (
    <article className="d2d-card" data-testid={`entry-${entry.id}`}>
      {hasPhotoSlot ? (
        <div className="d2d-card__photo" aria-hidden="true">
          {photo ? (
            <img
              className="d2d-card__img"
              src={photo}
              // 🔴 EMPTY ALT, AND THE WRAPPER IS `aria-hidden`. The photo says
              // nothing the title does not; a screen reader announcing
              // "image, https://cdn…" would be noise in place of the name of
              // the place. It is decoration for a link whose text is right
              // underneath it.
              alt=""
              loading="lazy"
              decoding="async"
              // The board's URL carries a trip id. A CDN does not need to know
              // which trip is looking at its photo, and `no-referrer` is the
              // one-word way to not tell it.
              referrerPolicy="no-referrer"
              onError={() => setPhotoBroken(true)}
              data-testid={`photo-${entry.id}`}
            />
          ) : null}
          {source ? <span className="d2d-card__source d2d-micro">{source}</span> : null}
        </div>
      ) : null}

      <div className="d2d-card__body">
        {!hasPhotoSlot ? (
          <p className="d2d-card__nophoto d2d-micro">no photo · added by hand</p>
        ) : null}

        <h3 className="d2d-card__title">
          {entry.url ? (
            // rel is not decoration here: a board is a list of links other
            // people pasted, and `noopener` is what stops the opened page
            // reaching back through window.opener.
            <a href={entry.url} target="_blank" rel="noopener noreferrer">
              {entry.title}
            </a>
          ) : (
            entry.title
          )}
        </h3>

        {/* 🔴 IN THE BODY, NOT IN THE PHOTO SLOT. That div is `aria-hidden`,
            so a button inside it would be a control no screen reader could
            reach and axe-core would (correctly) fail the page for it. The
            wording is a request, not a report: "try" is honest about a thing
            that may not work and claims nothing about why it did not. */}
        {onPreview && hasPhotoSlot && !photo && !readOnly ? (
          <p className="d2d-card__preview">
            <button
              type="button"
              className="d2d-card__previewbtn d2d-micro"
              disabled={busy || previewing}
              onClick={() => onPreview(entry)}
              data-testid={`preview-${entry.id}`}
            >
              {previewing ? 'Looking…' : 'Try for a preview'}
            </button>
          </p>
        ) : null}

        <div className="d2d-card__costline">
          {amount ? (
            <>
              <span className="d2d-card__amount">{amount}</span>
              <span className="d2d-card__basis d2d-micro">{basisLabel}</span>
            </>
          ) : (
            // The no-cost state, worded rather than blank. A blank might mean
            // free; this cannot be misread as one.
            <span className="d2d-card__nocost">nobody’s put a price on it yet</span>
          )}

          {enrichment?.distance === null ? (
            <span className="d2d-card__absent d2d-micro">distance unavailable</span>
          ) : enrichment?.distance ? (
            <span className="d2d-card__distance d2d-micro">{enrichment.distance}</span>
          ) : null}
        </div>

        {/* 🔴 A cost NOTE is not a cost. "£300ish each, the host said on the
            phone" carries no amount, is excluded from the roll-up by the
            service, and is shown as what it is — somebody's words, in quotes —
            rather than parsed into a figure. */}
        {entry.cost_note ? <p className="d2d-card__note">“{entry.cost_note}”</p> : null}

        <div className="d2d-card__foot">
          <div className="d2d-card__votes">
            {readOnly ? (
              <>
                <span className="d2d-tally">
                  <span aria-hidden="true">⭐</span>
                  <span className="d2d-sr-only">must-do</span>
                  <span className="d2d-vote__count">{mustCount}</span>
                </span>
                <span className="d2d-tally">
                  <span aria-hidden="true">👍</span>
                  <span className="d2d-sr-only">interested</span>
                  <span className="d2d-vote__count">{keenCount}</span>
                </span>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="d2d-vote"
                  data-level="must"
                  aria-pressed={mine === 'must'}
                  disabled={busy}
                  onClick={() => vote('must')}
                >
                  <span aria-hidden="true">⭐</span>
                  <span className="d2d-sr-only">must-do</span>
                  <span className="d2d-vote__count">{mustCount}</span>
                </button>
                <button
                  type="button"
                  className="d2d-vote"
                  data-level="interested"
                  aria-pressed={mine === 'interested'}
                  disabled={busy}
                  onClick={() => vote('interested')}
                >
                  <span aria-hidden="true">👍</span>
                  <span className="d2d-sr-only">interested</span>
                  <span className="d2d-vote__count">{keenCount}</span>
                </button>
              </>
            )}
          </div>

          <div className="d2d-card__by">
            <span className="d2d-card__byline">
              {addedByYou ? 'you added it' : 'added by the crew'}
            </span>
            <span
              className="d2d-avatar"
              style={{ background: `var(${avatarToken(entry.added_by)})` }}
              aria-hidden="true"
            />
          </div>
        </div>

        {readOnly ? null : (
          <button
            type="button"
            className="d2d-card__remove"
            disabled={busy}
            onClick={() => onRemove?.(entry)}
          >
            Take it off the board
          </button>
        )}
      </div>
    </article>
  )
}
